import { Component } from "@theme/component";
import { debounce, onAnimationEnd, onDocumentLoaded } from "@theme/utilities";

/**
 * A custom element that manages a drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} drawer – The drawer element.
 *
 * @extends Component<Refs>
 */
export class DrawerComponent extends Component {
  requiredRefs = ["drawer"];
  #isReady = false;
  #lastTrigger = null;
  #ancestorsWithAriaHidden = [];
  #scrollPosition = 0;

  connectedCallback() {
    super.connectedCallback();

    // Wait for DOM to be fully loaded before allowing drawer interactions
    onDocumentLoaded(() => {
      this.#isReady = true;
    });

    if (this.minWidth || this.maxWidth) {
      window.addEventListener("resize", this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener("resize", this.#handleResize);
    }
    // Clean up any restored aria-hidden attributes
    this.#restoreAriaHiddenOnAncestors();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDrawer();
    }
  }, 50);

  /**
   * Finds the first focusable element within the drawer.
   * @returns {HTMLElement|null} The first focusable element, or null if none found.
   */
  #getFirstFocusableElement() {
    const { drawer } = this.refs;
    const focusableSelector = "summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^='-']), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])";
    return drawer.querySelector(focusableSelector);
  }

  /**
   * Removes aria-hidden from ancestor elements to prevent accessibility violations
   * when focusable elements inside the drawer receive focus.
   */
  #removeAriaHiddenFromAncestors() {
    this.#ancestorsWithAriaHidden = [];
    let element = this.parentElement;

    while (element && element !== document.body) {
      if (element.hasAttribute('aria-hidden') && element.getAttribute('aria-hidden') === 'true') {
        // Store the element and its original value
        this.#ancestorsWithAriaHidden.push({
          element,
          value: 'true'
        });
        // Remove aria-hidden to allow focusable descendants
        element.removeAttribute('aria-hidden');
      }
      element = element.parentElement;
    }
  }

  /**
   * Locks body scroll with iOS-compatible method
   */
  #lockBodyScroll() {
    // Save current scroll position
    this.#scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Apply styles to prevent scroll on iOS
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollPosition}px`;
    document.body.style.width = '100%';
    
    // Prevent touch scrolling on iOS
    document.addEventListener('touchmove', this.#preventTouchMove, { passive: false });
  }

  /**
   * Unlocks body scroll and restores scroll position
   */
  #unlockBodyScroll() {
    // Remove touch event listener
    document.removeEventListener('touchmove', this.#preventTouchMove);

    // Restore body styles
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    // Restore scroll position
    window.scrollTo(0, this.#scrollPosition);
    this.#scrollPosition = 0;
  }

  /**
   * Prevents touch move events to disable scroll on iOS
   * @param {TouchEvent} event - The touch event
   */
  #preventTouchMove = (event) => {
    // Allow scrolling within drawer elements
    const target = event.target;
    if (target && this.refs.drawer?.contains(target)) {
      return;
    }
    // Prevent body scroll
    event.preventDefault();
  }

  /**
   * Restores aria-hidden attributes on ancestor elements.
   */
  #restoreAriaHiddenOnAncestors() {
    this.#ancestorsWithAriaHidden.forEach(({ element, value }) => {
      element.setAttribute('aria-hidden', value);
    });
    this.#ancestorsWithAriaHidden = [];
  }

  /**
   * Shows the drawer.
   * @param {Event} [event] - Optional triggering event
   */
  showDrawer(event) {
    // Don't allow interactions until DOM is ready
    if (!this.#isReady) {
      console.warn('[DrawerComponent] showDrawer called before DOM ready');
      return;
    }

    const { drawer } = this.refs;

    if (drawer.hasAttribute('open')) return;

    // Capture and store the trigger element for focus restoration
    this.#lastTrigger = event?.target;

    drawer.setAttribute('open', '');
    drawer.setAttribute('aria-hidden', 'false');

    // Remove aria-hidden from ancestors to prevent accessibility violations
    // when focusable elements inside the drawer receive focus
    this.#removeAriaHiddenFromAncestors();

    // Lock body scroll with iOS-compatible method
    this.#lockBodyScroll();

    // Pass triggering element in event detail
    const triggerElement = event?.target;
    this.dispatchEvent(new DrawerOpenEvent(triggerElement));

    // Add keyboard listener for Escape key
    document.addEventListener("keydown", this.#handleKeyDown);

    // Move focus to the first focusable element inside the drawer
    const firstFocusable = this.#getFirstFocusableElement();
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  /**
   * Closes the drawer.
   */
  closeDrawer = async () => {
    // Don't allow interactions until DOM is ready
    if (!this.#isReady) {
      console.warn('[DrawerComponent] closeDrawer called before DOM ready');
      return;
    }

    const { drawer } = this.refs;

    if (!drawer.hasAttribute('open')) return;

    document.removeEventListener("keydown", this.#handleKeyDown);

    drawer.classList.add("drawer-closing");

    await onAnimationEnd(drawer, undefined, {
      subtree: false,
    });

    drawer.removeAttribute('open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove("drawer-closing");

    // Restore aria-hidden on ancestors
    this.#restoreAriaHiddenOnAncestors();

    // Unlock body scroll with iOS-compatible method
    this.#unlockBodyScroll();

    this.dispatchEvent(new DrawerCloseEvent());

    // Restore focus to the trigger element and clear the stored reference
    if (this.#lastTrigger) {
      this.#lastTrigger.focus();
      this.#lastTrigger = null;
    }
  };

  /**
   * Toggles the drawer.
   */
  toggleDrawer = (event) => {
    // Don't allow interactions until DOM is ready
    if (!this.#isReady) {
      console.warn('[DrawerComponent] toggleDrawer called before DOM ready');
      return;
    }

    if (this.refs.drawer.hasAttribute('open')) {
      this.closeDrawer();
    } else {
      this.showDrawer(event);
    }
  };

  /**
   * Closes the drawer when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== "Escape") return;

    event.preventDefault();
    this.closeDrawer();
  };

  /**
   * Gets the minimum width of the drawer.
   *
   * @returns {number} The minimum width of the drawer.
   */
  get minWidth() {
    return Number(this.getAttribute("drawer-active-min-width"));
  }

  /**
   * Gets the maximum width of the drawer.
   *
   * @returns {number} The maximum width of the drawer.
   */
  get maxWidth() {
    return Number(this.getAttribute("drawer-active-max-width"));
  }
}

if (!customElements.get("drawer-component")) customElements.define("drawer-component", DrawerComponent);

export class DrawerOpenEvent extends CustomEvent {
  /**
   * @param {Element} [triggerElement] - The element that triggered the drawer open
   */
  constructor(triggerElement) {
    super(DrawerOpenEvent.eventName, {
      detail: { triggerElement },
      bubbles: true,
    });
  }

  static eventName = "drawer:open";
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, {
      bubbles: true,
    });
  }

  static eventName = "drawer:close";
}


